
import {computed, defineComponent, PropType} from 'vue';
import CropFilter, {
  supportPositions,
  supportModes,
} from '@/image-processor/filters/crop-filter';
import SequenceId from '@/utils/sequence-id';
import {useI18n} from 'vue-i18n';
import AbstractFilter from '@/image-processor/filters/abstract-filter';

type CropFilterProps = Omit<CropFilter, keyof AbstractFilter>;

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<CropFilterProps>,
      default: (): CropFilterProps => {
        return {
          mode: supportModes.RATIO,
          position: supportPositions.CENTER_MIDDLE,
          width: 1,
          height: 1,
        };
      },
    },
  },
  emits: {'update:modelValue': (data: CropFilterProps) => !!data},
  setup(props, {emit}) {
    const componentID = SequenceId.getNew();
    const {t} = useI18n({useScope: 'global'});
    const mode = computed({
      get: () => props.modelValue.mode,
      set: value =>
        emit('update:modelValue', {...props.modelValue, mode: value}),
    });

    const position = computed({
      get: () => props.modelValue.position,
      set: value =>
        emit('update:modelValue', {...props.modelValue, position: value}),
    });

    const width = computed({
      get: () => Number(props.modelValue.width),
      set: value =>
        emit('update:modelValue', {...props.modelValue, width: value}),
    });

    const height = computed({
      get: () => Number(props.modelValue.height),
      set: value =>
        emit('update:modelValue', {...props.modelValue, height: value}),
    });

    const inputSizeFormatter = (value: string | number): string => {
      return Number(value) === 0 ? '' : String(value);
    };

    const positionList = computed(() => {
      return Object.values(supportPositions).map(position => {
        return {
          text: t(`cropfilter.form.position.value.${position}`),
          value: position,
        };
      });
    });

    const modeList = computed(() => {
      return Object.values(supportModes).map(mode => {
        return {
          text: t(`cropfilter.form.mode.value.${mode}`),
          value: mode,
        };
      });
    });

    return {
      t,
      componentID,
      mode,
      position,
      width,
      height,
      positionList,
      modeList,
      inputSizeFormatter,
    };
  },
});
